import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="member-card" on a page's root element (see
// layouts/member.html.slim, chapter_admin.html.slim, chapter.html.slim) -
// one instance per page, not one per mention. Any descendant opts in with
// data-member-card plus data-member-card-* fields (see
// EventBodyHelper#linked_event_body, which writes these onto an `@mention`
// resolved to a real chapter member). On hover/focus, a business-card
// preview (business name/logo as the header, the mentioned person, phone/
// website, an about excerpt, "Read more") is portaled straight to
// document.body and positioned with getBoundingClientRect/position:fixed -
// same shape as tooltip_controller.js, just a richer bubble. Styled as
// .popover.card (see components/_popover.sass and cards/_base.sass) rather
// than bespoke chrome of its own.
export default class extends Controller {
  connect() {
    this.card = document.createElement("div")
    this.card.className = "member-card popover card padded light"
    // .card's own padding rule targets a direct child div (see
    // cards/_base.sass), so the actual row content lives in here rather
    // than directly on .card.
    this.content = document.createElement("div")
    this.content.className = "stack tight"
    this.card.appendChild(this.content)
    document.body.appendChild(this.card)

    this.onOver = this.onOver.bind(this)
    this.onOut = this.onOut.bind(this)
    this.onMouseMove = this.onMouseMove.bind(this)
    this.reposition = this.reposition.bind(this)

    this.element.addEventListener("mouseover", this.onOver)
    this.element.addEventListener("focusin", this.onOver)
    this.element.addEventListener("focusout", this.onOut)
    window.addEventListener("scroll", this.reposition, true)
    window.addEventListener("resize", this.reposition)
  }

  disconnect() {
    this.element.removeEventListener("mouseover", this.onOver)
    this.element.removeEventListener("focusin", this.onOver)
    this.element.removeEventListener("focusout", this.onOut)
    document.removeEventListener("mousemove", this.onMouseMove)
    window.removeEventListener("scroll", this.reposition, true)
    window.removeEventListener("resize", this.reposition)
    clearTimeout(this.hideTimeout)
    this.card.remove()
  }

  onOver(event) {
    const target = event.target.closest?.("[data-member-card]")
    if (!target || !this.element.contains(target)) return
    clearTimeout(this.hideTimeout)
    this.show(target)
  }

  onOut(event) {
    // Keyboard-only fallback (the card isn't reachable by tabbing off a
    // mouse-only mention link, but a real focus move away should still
    // close it). Mouse dismissal is handled by #onMouseMove instead - see
    // its comment for why.
    if (!this.activeTarget) return
    const related = event.relatedTarget
    if (related && (this.activeTarget.contains(related) || this.card.contains(related))) return
    this.scheduleHide()
  }

  // Dismissal is driven by where the cursor actually is, not by
  // mouseout/relatedTarget bookkeeping: the card is portaled to
  // document.body, so it's a sibling of the mention link rather than a
  // descendant, and relatedTarget-based containment checks only work
  // cleanly for a direct hop between the two - any real mouse path that
  // grazes other page content on the way (which is the common case, not
  // the exception, once the card has enough content to be worth reaching
  // for) fires mouseout events that don't fit that model. Checking the
  // cursor's actual position against both rects on every move sidesteps
  // that entirely.
  onMouseMove(event) {
    if (!this.activeTarget) return

    const pad = 12
    const inside = (rect) =>
      event.clientX >= rect.left - pad && event.clientX <= rect.right + pad &&
      event.clientY >= rect.top - pad && event.clientY <= rect.bottom + pad

    if (inside(this.activeTarget.getBoundingClientRect()) || inside(this.card.getBoundingClientRect())) {
      clearTimeout(this.hideTimeout)
    } else {
      this.scheduleHide()
    }
  }

  // A grace period, not an instant hide, so a moment's hesitation between
  // the mention link and the card doesn't flicker it closed.
  scheduleHide() {
    clearTimeout(this.hideTimeout)
    this.hideTimeout = setTimeout(() => this.hide(), 350)
  }

  show(target) {
    this.activeTarget = target
    this.render()
    this.card.classList.add("visible")
    document.addEventListener("mousemove", this.onMouseMove)
  }

  hide() {
    this.activeTarget = null
    this.card.classList.remove("visible")
    document.removeEventListener("mousemove", this.onMouseMove)
  }

  // Builds nodes via textContent rather than innerHTML template strings, so
  // a member/business name (or about excerpt) can never be interpreted as
  // markup.
  render() {
    if (!this.activeTarget) return
    const data = this.activeTarget.dataset
    // The mention link's own href is already the member's profile URL
    // (see EventBodyHelper#linked_event_body) - reuse it for every link in
    // the card rather than duplicating it server-side.
    const profileUrl = this.activeTarget.href

    const children = []

    if (data.memberCardIndustry) {
      const eyebrow = document.createElement("div")
      eyebrow.className = "member-card-eyebrow"
      eyebrow.textContent = data.memberCardIndustry
      children.push(eyebrow)
    }

    // The business is the card's subject - its name leads as the header,
    // not the mentioned person's.
    children.push(this.buildRow("member-card-header", this.buildLogo(data), data.memberCardBusiness, profileUrl))
    children.push(this.buildRow("member-card-person", this.buildAvatar(data), data.memberCardName))

    if (data.memberCardPhone) {
      const phone = document.createElement("a")
      phone.className = "member-card-contact"
      phone.href = `tel:${data.memberCardPhone}`
      phone.textContent = data.memberCardPhone
      children.push(phone)
    }

    if (data.memberCardWebsiteUrl) {
      const website = document.createElement("a")
      website.className = "member-card-contact"
      website.href = data.memberCardWebsiteUrl
      website.target = "_blank"
      website.rel = "noopener"
      website.textContent = data.memberCardWebsiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")
      children.push(website)
    }

    if (data.memberCardAbout) {
      const about = document.createElement("p")
      about.className = "member-card-about"
      about.textContent = data.memberCardAbout
      children.push(about)
    }

    const readMore = document.createElement("a")
    readMore.className = "member-card-more"
    readMore.href = profileUrl
    readMore.textContent = "Read more"
    children.push(readMore)

    this.content.replaceChildren(...children)
    this.reposition()
  }

  buildRow(className, image, text, href) {
    const row = document.createElement(href ? "a" : "div")
    row.className = className
    if (href) row.href = href
    if (image) row.appendChild(image)
    const label = document.createElement("span")
    label.textContent = text || ""
    row.appendChild(label)
    return row
  }

  buildAvatar(data) {
    const wrapper = document.createElement("span")
    wrapper.className = "member-card-avatar"
    if (data.memberCardAvatarUrl) {
      const img = document.createElement("img")
      img.src = data.memberCardAvatarUrl
      img.alt = ""
      wrapper.appendChild(img)
    } else {
      const initials = document.createElement("span")
      initials.className = "member-card-initials"
      initials.textContent = data.memberCardInitials || ""
      wrapper.appendChild(initials)
    }
    return wrapper
  }

  buildLogo(data) {
    if (!data.memberCardLogoUrl) return null
    const img = document.createElement("img")
    img.className = "member-card-logo"
    img.src = data.memberCardLogoUrl
    img.alt = ""
    return img
  }

  reposition() {
    if (!this.activeTarget) return

    const targetRect = this.activeTarget.getBoundingClientRect()
    const cardRect = this.card.getBoundingClientRect()
    const gap = 8

    let top = targetRect.top - cardRect.height - gap
    if (top < 0) top = targetRect.bottom + gap

    let left = targetRect.left
    left = Math.max(4, Math.min(left, window.innerWidth - cardRect.width - 4))

    this.card.style.top = `${top}px`
    this.card.style.left = `${left}px`
  }
}
